<template>
    <v-container fluid>
        <v-row class="mx-0 ">
            <v-col cols="12" sm="4" md="3" lg="2">
                <v-select
                    v-model="selectedYear"
                    :items="years"
                    label="Year"
                    dense
                    hide-details
                    class="title mx-2"
                >
                </v-select>
            </v-col>
            <v-col cols="12" sm="2" md="2">
                <v-select
                    ref="v_select_folder"
                    dense
                    hide-details
                    class="title mx-2"
                    v-model="selectedPlanFolder"
                    label="Plan Folder"
                    :items="tenantPlannedFolder"
                    item-value="node.id"
                    item-text="node.name"
                    return-object
                >
                </v-select>
            </v-col>
            <v-col class="title">
                P & L
            </v-col>
        </v-row>

        <v-card class="mx-3">
            <v-data-table
                dense
                :headers="headers"
                :items="plRevenueData"
                :loading="isLoading"
                sort-by="greenhouse"
                disable-pagination
                hide-default-footer
                class="pa-3"
            >
                <!-- Format values -->
                <template v-for="mon in months" v-slot:[mon.m_slot]="{ item }">
                    {{ item[mon.m_text].toLocaleString() }}
                </template>
                <template v-slot:item.fullYear="{ item }">
                    {{ item.fullYear.toLocaleString() }}
                </template>

                <!-- Total Revenue -->
                <template slot="body.append">
                    <tr class="">
                        <th class="subtitle-1">Estimated Total Revenue</th>
                        <th></th>
                        <th
                            v-for="m in months"
                            :key="m.m_value"
                            class="text-right subtitle-2 success--text"
                        >
                            {{
                                sumField(
                                    plRevenueData,
                                    m.m_text
                                ).toLocaleString()
                            }}
                        </th>
                        <th
                            class="text-right subtitle-2 font-weight-black success--text"
                        >
                            {{
                                sumField(
                                    plRevenueData,
                                    "fullYear"
                                ).toLocaleString()
                            }}
                        </th>
                    </tr>
                </template>
            </v-data-table>

            <v-data-table
                dense
                :headers="headers"
                :items="plCostData"
                :loading="isLoading"
                sort-by="greenhouse"
                item-key="node.id"
                disable-pagination
                hide-default-footer
                class="pa-3"
            >
                <!-- Format values -->
                <template v-for="mon in months" v-slot:[mon.m_slot]="{ item }">
                    {{ item[mon.m_text].toLocaleString() }}
                </template>
                <template v-slot:item.fullYear="{ item }">
                    {{ item.fullYear.toLocaleString() }}
                </template>

                <!-- Total Cost -->
                <template slot="body.append">
                    <tr class="">
                        <th class="subtitle-1">Estimated Total Expenses</th>
                        <th></th>
                        <th
                            v-for="m in months"
                            :key="m.m_value"
                            class="text-right subtitle-2 pink--text"
                        >
                            {{
                                sumField(plCostData, m.m_text).toLocaleString()
                            }}
                        </th>
                        <th
                            class="text-right subtitle-2 font-weight-black pink--text"
                        >
                            {{
                                sumField(
                                    plCostData,
                                    "fullYear"
                                ).toLocaleString()
                            }}
                        </th>
                    </tr>
                </template>
            </v-data-table>

            <v-data-table
                dense
                :headers="headers"
                hide-default-footer
                class="pa-3"
            >
                <!-- Total Profit -->
                <template slot="body">
                    <tr>
                        <th class="subtitle-1" width="15%">
                            Estimated Total Profit
                        </th>
                        <th width="10%"></th>
                        <th
                            v-for="m in months"
                            :key="m.m_value"
                            class="text-right subtitle-2"
                            :set="
                                (profit =
                                    sumField(plRevenueData, m.m_text) -
                                    sumField(plCostData, m.m_text))
                            "
                        >
                            <span v-if="profit < 0" class="pink--text">{{
                                profit.toLocaleString()
                            }}</span>
                            <span v-else-if="profit == 0">{{
                                profit.toLocaleString()
                            }}</span>
                            <span v-else class="success--text">{{
                                profit.toLocaleString()
                            }}</span>
                        </th>
                        <th
                            class="text-right subtitle-2 font-weight-black"
                            :set="
                                (totalProtif =
                                    sumField(plRevenueData, 'fullYear') -
                                    sumField(plCostData, 'fullYear'))
                            "
                        >
                            <span v-if="totalProtif < 0" class="pink--text">{{
                                totalProtif.toLocaleString()
                            }}</span>
                            <span v-else-if="totalProtif == 0">{{
                                totalProtif.toLocaleString()
                            }}</span>
                            <span v-else class="success--text">{{
                                totalProtif.toLocaleString()
                            }}</span>
                        </th>
                    </tr>
                </template>
            </v-data-table>
        </v-card>
    </v-container>
</template>


<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";

export default {
    name: "pl-table",
    props: [],
    components: {},

    apollo: {
        planFolders: {
            query: gql`
                query tenantPlannedFolder {
                    tenantPlannedFolder {
                        edges {
                            node {
                                id
                                name
                            }
                        }
                    }
                }
            `,
            variables() {},
            fetchPolicy: "cache-and-network",
            update(data) {
                this.tenantPlannedFolder = JSON.parse(
                    JSON.stringify(data.tenantPlannedFolder.edges)
                );
                this.tenantPlannedFolder.unshift({
                    node: { id: null, name: "Default" }
                });
                return data.tenantPlannedFolder.edges;
            }
        }
    },

    data: function() {
        return {
            isLoading: false,
            selectedYear: null,
            years: [],
            tenantGreenhousePlannedFinanceByMonth: {},

            plRevenueData: [],
            plCostData: [],

            tenantPlannedFolder: [],
            defaultPlanFolder: { node: { id: null, name: "Default" } },
            selectedPlanFolder: { node: { id: null, name: "Default" } },
            selectedPlanFolderID: null,
            selectedPlanFolderName: "Default",

            months: [
                { m_text: "jan", m_slot: "item.jan", m_value: 1 },
                { m_text: "feb", m_slot: "item.feb", m_value: 2 },
                { m_text: "mar", m_slot: "item.mar", m_value: 3 },
                { m_text: "apr", m_slot: "item.apr", m_value: 4 },
                { m_text: "may", m_slot: "item.may", m_value: 5 },
                { m_text: "jun", m_slot: "item.jun", m_value: 6 },
                { m_text: "jul", m_slot: "item.jul", m_value: 7 },
                { m_text: "aug", m_slot: "item.aug", m_value: 8 },
                { m_text: "sep", m_slot: "item.sep", m_value: 9 },
                { m_text: "oct", m_slot: "item.oct", m_value: 10 },
                { m_text: "nov", m_slot: "item.nov", m_value: 11 },
                { m_text: "dec", m_slot: "item.dec", m_value: 12 }
            ],

            headers: [
                {
                    text: "Name",
                    value: "financeName",
                    width: "15%"
                },
                {
                    text: "Greenhouse",
                    value: "greenhouse",
                    width: "10%"
                },
                { text: "Jan", value: "jan", align: "end" },
                { text: "Feb", value: "feb", align: "end" },
                { text: "Mar", value: "mar", align: "end" },
                { text: "Apr", value: "apr", align: "end" },
                { text: "May", value: "may", align: "end" },
                { text: "Jun", value: "jun", align: "end" },
                { text: "Jul", value: "jul", align: "end" },
                { text: "Aug", value: "aug", align: "end" },
                { text: "Sep", value: "sep", align: "end" },
                { text: "Oct", value: "oct", align: "end" },
                { text: "Nov", value: "nov", align: "end" },
                { text: "Dec", value: "dec", align: "end" },
                {
                    text: "Full year",
                    value: "fullYear",
                    align: "end"
                }
            ]
        };
    },

    computed: {},
    watch: {
        selectedYear(val) {
            if (val) {
                this.getGreenhousePlannedFinanceByMonth();
            }
        },
        selectedPlanFolder: {
            handler(n) {
                if (n) {
                    this.selectedPlanFolderName = n.node.name;
                    this.selectedPlanFolderID = n.node.id;
                    localStorage.setItem(
                        "planningToolSelectedFolder",
                        JSON.stringify(n)
                    );
                    if (n.node.id != null) this.planFolderNullFilter = false;
                    else this.planFolderNullFilter = true;
                    this.getGreenhousePlannedFinanceByMonth();
                }
            },
            deep: true
        }
    },
    created() {
        this.hasPermission = helper.hasPermission;
    },
    mounted() {
        // initialize taskYear list and selectedYear
        let _thisYear = new Date().getFullYear();

        this.years = Array.from({ length: 5 }, (_, i) => _thisYear - 2 + i);
        this.selectedYear = _thisYear;

        const _planningToolSelectedFolder = localStorage.getItem(
            "planningToolSelectedFolder"
        );

        if (_planningToolSelectedFolder) {
            this.selectedPlanFolder = JSON.parse(_planningToolSelectedFolder);
            // this.setFolderNameAndID(this.selectedPlanFolder);
        }
    },
    methods: {
        sumField(dataArray, key) {
            // sum data in give key (property)
            return dataArray.reduce((a, b) => a + (b[key] || 0), 0);
        },

        getGreenhousePlannedFinanceByMonth() {
            if (!this.selectedYear) {
                return;
            }

            this.isLoading = true;

            this.$apollo
                .query({
                    // Query
                    query: gql`
                        query tenantGreenhousePlannedFinanceByMonth(
                            $year: Int!
                            $planFolder: ID
                        ) {
                            tenantGreenhousePlannedFinanceByMonth(
                                year: $year
                                planFolder: $planFolder
                            ) {
                                edges {
                                    node {
                                        id
                                        year
                                        month
                                        greenhouse {
                                            id
                                            name
                                        }
                                        youngPlantCost
                                        growthCost
                                        productCost
                                        totalBusinessCost
                                        totalRevenue
                                        totalProfit
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        year: this.selectedYear,
                        planFolder: this.selectedPlanFolderID
                    },
                    fetchPolicy: "network-only"
                })
                .then(response => {
                    // Result

                    const _tenantGreenhousePlannedFinanceByMonth = JSON.parse(
                        JSON.stringify(
                            response.data.tenantGreenhousePlannedFinanceByMonth
                        )
                    );

                    this.tenantGreenhousePlannedFinanceByMonth = _tenantGreenhousePlannedFinanceByMonth;

                    this.plRevenueData = [];
                    this.plCostData = [];

                    _tenantGreenhousePlannedFinanceByMonth.edges.forEach(
                        item => {
                            const _month = this.months[item.node.month - 1];

                            let _row = {};

                            //// total revenue
                            _row = this.plRevenueData.find(x => {
                                return (
                                    x.greenhouse ===
                                        item.node.greenhouse.name &&
                                    x.financeName == "Revenue"
                                );
                            });

                            const _totalRevenue = Math.round(
                                item.node.totalRevenue
                            );

                            if (_row) {
                                _row[_month.m_text] = _totalRevenue;
                                _row.fullYear += _totalRevenue;
                            } else {
                                _row = {};
                                _row.greenhouse = item.node.greenhouse.name;
                                _row.financeName = "Revenue";
                                _row[_month.m_text] = _totalRevenue;
                                _row.fullYear = _totalRevenue;

                                this.plRevenueData.push(_row);
                            }

                            //// business cost
                            _row = null;
                            _row = this.plCostData.find(x => {
                                return (
                                    x.greenhouse ===
                                        item.node.greenhouse.name &&
                                    x.financeName == "Business Cost"
                                );
                            });

                            const _totalBusinessCost = Math.round(
                                item.node.totalBusinessCost
                            );

                            if (_row) {
                                _row[_month.m_text] = _totalBusinessCost;
                                _row.fullYear += _totalBusinessCost;
                            } else {
                                _row = {};
                                _row.greenhouse = item.node.greenhouse.name;
                                _row.financeName = "Business Cost";
                                _row[_month.m_text] = _totalBusinessCost;
                                _row.fullYear = _totalBusinessCost;

                                this.plCostData.push(_row);
                            }

                            //// growth cost
                            _row = null;
                            _row = this.plCostData.find(x => {
                                return (
                                    x.greenhouse ===
                                        item.node.greenhouse.name &&
                                    x.financeName == "Growth Cost"
                                );
                            });

                            const _growthCost = Math.round(
                                item.node.youngPlantCost + item.node.growthCost
                            );

                            if (_row) {
                                _row[_month.m_text] = _growthCost;
                                _row.fullYear += _growthCost;
                            } else {
                                _row = {};
                                _row.greenhouse = item.node.greenhouse.name;
                                _row.financeName = "Growth Cost";
                                _row[_month.m_text] = _growthCost;
                                _row.fullYear = _growthCost;

                                this.plCostData.push(_row);
                            }

                            //// product cost
                            _row = null;
                            _row = this.plCostData.find(x => {
                                return (
                                    x.greenhouse ===
                                        item.node.greenhouse.name &&
                                    x.financeName == "Product Cost"
                                );
                            });

                            const _productCost = Math.round(
                                item.node.productCost
                            );

                            if (_row) {
                                _row[_month.m_text] = _productCost;
                                _row.fullYear += _productCost;
                            } else {
                                _row = {};
                                _row.greenhouse = item.node.greenhouse.name;
                                _row.financeName = "Product Cost";
                                _row[_month.m_text] = _productCost;
                                _row.fullYear = _productCost;

                                this.plCostData.push(_row);
                            }
                        }
                    );
                })
                .catch(error => {
                    // Error
                    console.error(error);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }
    }
};
</script>